/**
 * PLZ → Stadt-Vorschlag + Validierung für Customer-, Online-Booking- und
 * Calendar-Adressformulare.
 *
 * Verdrahtung im Template:
 *   <form data-postal-lookup>
 *       <input data-postal-input>
 *       <input data-city-input>
 *       <select data-country-input>            (optional, default "DE")
 *   </form>
 *
 * Verhalten:
 *   - PLZ-Input → debounced Lookup; bei genau 1 Treffer und leerem City-Feld
 *     wird der Treffer still eingetragen, bei N Treffern als <datalist>-Option
 *     vorgeschlagen.
 *   - City-Blur → Validate-Call. Bei Mismatch erscheint ein inline-Hint mit
 *     „Übernehmen"-Button (nur wenn ein eindeutiger best_match vorliegt).
 *   - Keine Submit-Blockade, kein automatisches Überschreiben getippter Werte.
 */

export interface CitySuggestion {
    name: string;
    admin1: string | null;
    latitude: number | null;
    longitude: number | null;
}

export type ValidationStatus = 'valid' | 'mismatch' | 'unknown_postal_code';

export interface ValidationResponse {
    status: ValidationStatus;
    suggestions: CitySuggestion[];
    best_match: CitySuggestion | null;
}

interface ElementRefs {
    form: HTMLElement;
    postalInput: HTMLInputElement;
    cityInput: HTMLInputElement;
    countryInput: HTMLInputElement | HTMLSelectElement | null;
}

const LOOKUP_URL = '/api/postal-code/lookup.php';
const VALIDATE_URL = '/api/postal-code/validate.php';
const DEBOUNCE_MS = 300;
const MIN_POSTAL_LENGTH = 3;

export class PostalCodeLookup {
    private postalDebounce: number | null = null;
    private lastAutoFilledCity: string | null = null;
    private hintElement: HTMLElement | null = null;
    private datalist: HTMLDataListElement | null = null;
    private datalistId: string;

    constructor(private readonly refs: ElementRefs) {
        this.datalistId = `postal-cities-${Math.random().toString(36).slice(2)}`;
    }

    static initAll(root: ParentNode = document): PostalCodeLookup[] {
        const instances: PostalCodeLookup[] = [];

        for (const form of root.querySelectorAll<HTMLElement>('[data-postal-lookup]')) {
            const postalInput = form.querySelector<HTMLInputElement>('[data-postal-input]');
            const cityInput = form.querySelector<HTMLInputElement>('[data-city-input]');
            const countryInput = form.querySelector<HTMLInputElement | HTMLSelectElement>(
                '[data-country-input]',
            );

            if (!postalInput || !cityInput) {
                continue;
            }

            const instance = new PostalCodeLookup({ form, postalInput, cityInput, countryInput });
            instance.init();
            instances.push(instance);
        }

        return instances;
    }

    init(): void {
        this.attachDatalist();

        this.refs.postalInput.addEventListener('input', () => {
            this.scheduleSuggest();
        });

        this.refs.cityInput.addEventListener('blur', () => {
            void this.validate();
        });
    }

    private attachDatalist(): void {
        this.datalist = document.createElement('datalist');
        this.datalist.id = this.datalistId;
        this.refs.cityInput.setAttribute('list', this.datalistId);
        this.refs.cityInput.parentElement?.append(this.datalist);
    }

    private scheduleSuggest(): void {
        if (this.postalDebounce !== null) {
            window.clearTimeout(this.postalDebounce);
        }
        this.postalDebounce = window.setTimeout(() => {
            void this.suggest();
        }, DEBOUNCE_MS);
    }

    private async suggest(): Promise<void> {
        const postal = this.refs.postalInput.value.trim();
        const country = this.currentCountry();

        if (postal.length < MIN_POSTAL_LENGTH) {
            this.setDatalist([]);
            return;
        }

        const cities = await this.fetchCities(country, postal);
        this.setDatalist(cities);

        if (cities.length === 1 && this.cityIsEmptyOrAutoFilled()) {
            this.applySuggestion(cities[0]);
        }
    }

    private async validate(): Promise<void> {
        const postal = this.refs.postalInput.value.trim();
        const country = this.currentCountry();
        const city = this.refs.cityInput.value.trim();

        this.clearHint();

        if (postal.length < MIN_POSTAL_LENGTH || city === '') {
            return;
        }

        const result = await this.fetchValidation(country, postal, city);

        if (!result || result.status !== 'mismatch' || !result.best_match) {
            return;
        }

        this.showMismatchHint(result.best_match);
    }

    private currentCountry(): string {
        const value = this.refs.countryInput?.value?.trim() ?? '';
        return value !== '' ? value.toUpperCase() : 'DE';
    }

    private cityIsEmptyOrAutoFilled(): boolean {
        const current = this.refs.cityInput.value.trim();
        return current === '' || current === this.lastAutoFilledCity;
    }

    private applySuggestion(city: CitySuggestion): void {
        this.refs.cityInput.value = city.name;
        this.lastAutoFilledCity = city.name;
        this.refs.cityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    private setDatalist(cities: CitySuggestion[]): void {
        if (!this.datalist) {
            return;
        }
        this.datalist.replaceChildren();
        for (const city of cities) {
            const option = document.createElement('option');
            option.value = city.name;
            if (city.admin1 !== null && city.admin1 !== city.name) {
                option.label = city.admin1;
            }
            this.datalist.append(option);
        }
    }

    private clearHint(): void {
        this.hintElement?.remove();
        this.hintElement = null;
    }

    private showMismatchHint(bestMatch: CitySuggestion): void {
        this.clearHint();

        const hint = document.createElement('div');
        hint.setAttribute('data-postal-hint', '');
        hint.className = 'mt-1 text-sm text-amber-700 flex items-center gap-2';

        const text = document.createElement('span');
        text.textContent = 'Meintest du ';
        const strong = document.createElement('strong');
        strong.textContent = bestMatch.name;
        text.append(strong, '?');

        const apply = document.createElement('button');
        apply.type = 'button';
        apply.className = 'underline hover:text-amber-900';
        apply.textContent = 'Übernehmen';
        apply.addEventListener('click', () => {
            this.applySuggestion(bestMatch);
            this.clearHint();
        });

        hint.append(text, apply);
        this.hintElement = hint;
        this.refs.cityInput.parentElement?.append(hint);
    }

    private async fetchCities(country: string, postal: string): Promise<CitySuggestion[]> {
        try {
            const response = await fetch(this.buildUrl(LOOKUP_URL, { country, postal_code: postal }), {
                headers: { Accept: 'application/json' },
            });
            if (!response.ok) {
                return [];
            }
            const data = (await response.json()) as { cities?: CitySuggestion[] };
            return Array.isArray(data.cities) ? data.cities : [];
        } catch {
            return [];
        }
    }

    private async fetchValidation(
        country: string,
        postal: string,
        city: string,
    ): Promise<ValidationResponse | null> {
        try {
            const response = await fetch(
                this.buildUrl(VALIDATE_URL, { country, postal_code: postal, city }),
                { headers: { Accept: 'application/json' } },
            );
            if (!response.ok) {
                return null;
            }
            return (await response.json()) as ValidationResponse;
        } catch {
            return null;
        }
    }

    private buildUrl(base: string, params: Record<string, string>): string {
        const query = new URLSearchParams(params);
        return `${base}?${query.toString()}`;
    }
}

if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => PostalCodeLookup.initAll());
    } else {
        PostalCodeLookup.initAll();
    }
}
